import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useI18n } from "../../i18n/LanguageProvider";
import { Container } from "../Container";
import { scrollToId } from "../AppButtons";
import { Lock, ChevronRight, Clock, ReceiptText, Check, ChevronDown, ArrowUpRight, TrendingUp } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];
const GREEN_BG = "#C6F2DD";
const DARK = "#0A3D28";

/** Animated number that counts up when scrolled into view. */
function CountUp({ value, decimals = 0, prefix = "", suffix = "" }) {
  const { locale } = useI18n();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 950;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  const formatted = new Intl.NumberFormat(locale || "es", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(display);
  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const DarkPill = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="focus-ring group inline-flex w-full max-w-md items-center justify-center gap-2 rounded-full bg-[#0A3D28] px-8 py-4 text-base font-semibold text-green transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]"
  >
    {children}
  </button>
);

function ComparisonCard({ t }) {
  const rows = t("product2.p1rows");
  return (
    <div className="overflow-hidden rounded-[2rem] bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.3)]">
      <div className="flex items-start justify-between bg-[#0A3D28] px-6 py-6 text-white sm:px-8">
        <span className="max-w-[55%] text-sm font-medium leading-snug text-white/70">{t("product2.p1cardTitle")}</span>
        <span className="text-sm font-medium text-white/70">{t("product2.p1cardCol")}</span>
      </div>
      {rows.map((r, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.5, ease, delay: 0.15 + i * 0.12 }}
          className="flex items-center justify-between border-b border-black/[0.06] px-6 py-5 last:border-0 sm:px-8"
        >
          <span className={`text-lg font-bold tracking-tight ${i === 0 ? "text-green" : "text-black/70"}`}>{r.name}</span>
          <div className="flex items-center gap-3 text-right">
            <div>
              <div className="text-lg font-bold text-black">{r.amount}</div>
              {r.delta && <div className="text-sm font-semibold text-red-500">▼ {r.delta}</div>}
            </div>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.05] text-black/40">
              <ChevronDown className="h-4 w-4" strokeWidth={2} />
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ChargeCard({ t }) {
  return (
    <div className="rounded-[2rem] bg-white p-6 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.3)] sm:p-8">
      <div className="mb-5 flex justify-end">
        <span className="inline-flex items-center gap-2 rounded-full bg-black/[0.04] px-4 py-2 text-sm font-medium text-black/50">
          <Lock className="h-3.5 w-3.5" strokeWidth={2} /> {t("product2.p2policy")} <ChevronRight className="h-4 w-4" strokeWidth={2} />
        </span>
      </div>
      <div className="text-base text-black/60">{t("product2.p2sendLabel")}</div>
      <div className="mt-1 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 rounded-full bg-black/[0.04] px-3 py-2 text-sm font-semibold text-black">
          <span className="flex h-6 w-6 items-center justify-center rounded-full brand-gradient text-[10px] text-white">C</span> {t("product2.p2client")}
        </span>
        <span className="text-3xl font-bold tracking-tight text-black"><CountUp value={420} decimals={2} suffix=" €" /></span>
      </div>
      <div className="my-4 rounded-2xl bg-green/10 px-4 py-3 text-sm leading-relaxed text-[#0A3D28]">{t("product2.p2info")}</div>
      <div className="text-base text-black/60">{t("product2.p2recipLabel")}</div>
      <div className="mt-1 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 rounded-full bg-black/[0.04] px-3 py-2 text-sm font-semibold text-black">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0A3D28] text-[10px] text-green">T</span> {t("product2.p2you")}
        </span>
        <span className="text-3xl font-bold tracking-tight text-black"><CountUp value={420} decimals={2} suffix=" €" /></span>
      </div>
      <div className="mt-5 space-y-4 border-t border-black/[0.06] pt-5">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-black/50"><Clock className="h-4 w-4" strokeWidth={2} /></span>
          <div><div className="text-sm text-black/45">{t("product2.p2arrives")}</div><div className="font-semibold text-black">{t("product2.p2arrivesVal")}</div></div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-black/50"><ReceiptText className="h-4 w-4" strokeWidth={2} /></span>
          <div><div className="text-sm text-black/45">{t("product2.p2fees")}</div><div className="font-semibold text-black">{t("product2.p2feesVal")}</div></div>
        </div>
      </div>
    </div>
  );
}

function RecoveryCard({ t }) {
  const bars = [28, 42, 38, 60, 76, 95];
  return (
    <div className="rounded-[2rem] bg-white p-6 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.3)] sm:p-8">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-sm text-black/50">{t("product2.p4cardLabel")}</div>
          <div className="text-4xl font-bold tracking-tight text-[#0A3D28]"><CountUp value={4820} prefix="€" /></div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green/10 px-3 py-1.5 text-sm font-semibold text-green">
          <TrendingUp className="h-4 w-4" strokeWidth={2.5} /> +38%
        </span>
      </div>
      <div className="mt-6 flex h-40 items-end gap-2 sm:gap-2.5">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0, opacity: 0.3 }}
            whileInView={{ height: `${h}%`, opacity: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.7, ease, delay: 0.1 + i * 0.09 }}
            className={`flex-1 rounded-t-lg ${i === bars.length - 1 ? "brand-gradient" : "bg-green/25"}`}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[10px] font-medium text-black/35">
        {["E", "F", "M", "A", "M", "J"].map((m, i) => (
          <span key={i} className="flex-1 text-center">{m}</span>
        ))}
      </div>
      <p className="mt-5 border-t border-black/[0.06] pt-4 text-sm leading-relaxed text-black/55">{t("product2.p4foot")}</p>
    </div>
  );
}

function DoMoreCard({ t }) {
  return (
    <div className="rounded-[2rem] bg-white p-6 text-center shadow-[0_40px_90px_-30px_rgba(0,0,0,0.3)] sm:p-8">
      <div className="relative mx-auto max-w-xs">
        <div className="absolute -right-3 -top-3 h-full w-full rounded-2xl bg-black/[0.04]" />
        <div className="absolute -right-1.5 -top-1.5 h-full w-full rounded-2xl bg-black/[0.06]" />
        <div className="relative rounded-2xl bg-[#EAF7F0] p-5 text-left">
          <div className="flex items-center justify-between">
            <span className="text-sm text-black/50">{t("product2.p3cardLabel")}</span>
            <span className="h-6 w-6 rounded-full brand-gradient" />
          </div>
          <div className="mt-1 text-4xl font-bold tracking-tight text-[#0A3D28]"><CountUp value={1181.05} decimals={2} prefix="€" /></div>
          <div className="mt-3 text-xs font-medium text-black/40">ES21 · 9672 0617 7203</div>
        </div>
      </div>
      <h4 className="mt-8 text-2xl font-bold tracking-tight text-black">{t("product2.p3feat")}</h4>
      <p className="mx-auto mt-2 max-w-sm text-base leading-relaxed text-black/55">{t("product2.p3featDesc")}</p>
      <div className="mt-4 inline-flex items-center gap-2 font-semibold text-green">
        <Check className="h-5 w-5 rounded-full bg-green/15 p-0.5" strokeWidth={2.5} /> <span className="underline decoration-2 underline-offset-4">{t("product2.p3check")}</span>
      </div>
      <button type="button" onClick={() => scrollToId("features")} className="focus-ring mt-6 w-full rounded-full border-2 border-[#0A3D28] py-3.5 text-base font-semibold text-[#0A3D28] transition-colors hover:bg-[#0A3D28] hover:text-green">
        {t("product2.p3cta2")}
      </button>
    </div>
  );
}

function Panel({ bg, align = "center", children }) {
  return (
    <div className="px-5 py-24 md:py-32" style={{ backgroundColor: bg }}>
      <Container>
        <div className={`mx-auto max-w-2xl ${align === "center" ? "text-center" : ""}`}>{children.head}</div>
        <Reveal delay={0.1} className="mx-auto mt-14 max-w-lg">{children.card}</Reveal>
      </Container>
    </div>
  );
}

export function ProductScreens() {
  const { t } = useI18n();

  return (
    <section id="product" className="scroll-mt-24">
      {/* Panel 1 — comparison */}
      <Panel bg={GREEN_BG}>
        {{
          head: (
            <>
              <Reveal><h2 className="text-4xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl" style={{ color: DARK }}>{t("product2.p1title")}</h2></Reveal>
              <Reveal delay={0.08}><p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed" style={{ color: DARK }}>{t("product2.p1sub")}</p></Reveal>
              <Reveal delay={0.14} className="mt-9 flex flex-col items-center gap-4">
                <DarkPill onClick={() => scrollToId("download")} testId="product-p1-cta">{t("product2.p1cta")}</DarkPill>
                <button type="button" onClick={() => scrollToId("policies")} className="text-base font-semibold underline decoration-2 underline-offset-4" style={{ color: DARK }}>{t("product2.p1link")}</button>
              </Reveal>
            </>
          ),
          card: <ComparisonCard t={t} />,
        }}
      </Panel>

      {/* Panel 2 — charge card */}
      <Panel bg={GREEN_BG} align="left">
        {{
          head: (
            <>
              <Reveal><h2 className="text-4xl font-extrabold leading-[1] tracking-tight sm:text-5xl lg:text-6xl" style={{ color: DARK }}>{t("product2.p2title")}</h2></Reveal>
              <Reveal delay={0.08}><p className="mt-6 max-w-xl text-lg font-medium leading-relaxed underline decoration-green decoration-2 underline-offset-4" style={{ color: DARK }}>{t("product2.p2sub")}</p></Reveal>
              <Reveal delay={0.14} className="mt-9"><DarkPill onClick={() => scrollToId("policies")} testId="product-p2-cta">{t("product2.p2cta")}</DarkPill></Reveal>
            </>
          ),
          card: <ChargeCard t={t} />,
        }}
      </Panel>

      {/* Panel 3 — recovered late payments */}
      <Panel bg="#EFFBF5">
        {{
          head: (
            <>
              <Reveal><h2 className="text-4xl font-extrabold leading-[1] tracking-tight text-black sm:text-5xl lg:text-6xl">{t("product2.p4title")}</h2></Reveal>
              <Reveal delay={0.08}><p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-black/60">{t("product2.p4sub")}</p></Reveal>
              <Reveal delay={0.14} className="mt-9 flex justify-center">
                <DarkPill onClick={() => scrollToId("policies")} testId="product-p4-cta">{t("product2.p4cta")}</DarkPill>
              </Reveal>
            </>
          ),
          card: <RecoveryCard t={t} />,
        }}
      </Panel>

      {/* Panel 4 — do more */}
      <Panel bg={GREEN_BG}>
        {{
          head: (
            <>
              <Reveal><h2 className="text-4xl font-extrabold leading-[1] tracking-tight sm:text-5xl lg:text-6xl" style={{ color: DARK }}>{t("product2.p3title")}</h2></Reveal>
              <Reveal delay={0.08}><p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed" style={{ color: DARK }}>{t("product2.p3sub")}</p></Reveal>
              <Reveal delay={0.14} className="mt-9 flex justify-center">
                <button type="button" onClick={() => scrollToId("download")} data-testid="product-p3-cta" className="focus-ring inline-flex w-full max-w-md items-center justify-center gap-2 rounded-full brand-gradient px-8 py-4 text-base font-semibold text-white transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]">
                  {t("product2.p3cta")} <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </Reveal>
            </>
          ),
          card: <DoMoreCard t={t} />,
        }}
      </Panel>
    </section>
  );
}
